import React, { useEffect, useId, useMemo, useRef, useState } from 'react'

import {
  FieldContainer,
  FieldLabel,
  FieldMessage,
} from '@components/formField/formField.styled'

import {
  CalendarBlank,
  CalendarDay,
  CalendarDayContainer,
  CalendarDayIndicator,
  CalendarDayIndicators,
  CalendarDayTooltip,
  CalendarGrid,
  CalendarHeader,
  CalendarMark,
  CalendarNavigationButton,
  CalendarPopover,
  CalendarLegend,
  CalendarLegendItem,
  CalendarWeek,
  CalendarWeekday,
  DatePickerContainer,
  DatePickerTrigger,
} from './datePicker.styled'

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

export function parseDateValue(value?: string) {
  const match = value?.match(DATE_PATTERN)
  if (!match) return null
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  )
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDateValue(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

function sameDay(left: Date | null, right: Date) {
  return Boolean(left && formatDateValue(left) === formatDateValue(right))
}

function monthDays(month: Date) {
  const year = month.getFullYear()
  const monthIndex = month.getMonth()
  const firstWeekday = (new Date(year, monthIndex, 1).getDay() + 6) % 7
  const count = new Date(year, monthIndex + 1, 0).getDate()
  const monthKey = `${year}-${monthIndex}`
  const days: Array<{ key: string; date: Date | null }> = Array.from(
    { length: firstWeekday },
    (_, offset) => ({
      key: `${monthKey}-leading-${offset}`,
      date: null,
    })
  )
  for (let day = 1; day <= count; day += 1) {
    const date = new Date(year, monthIndex, day)
    days.push({ key: formatDateValue(date), date })
  }
  let trailing = 0
  while (days.length % 7 !== 0) {
    days.push({
      key: `${monthKey}-trailing-${trailing}`,
      date: null,
    })
    trailing += 1
  }
  return days
}

export type DatePickerProps = {
  label: string
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  locale?: string
  disabled?: boolean
  required?: boolean
  error?: string
  hint?: string
  className?: string
  getDayInfo?: (date: Date) => CalendarDayInfo | undefined
}

export type CalendarDayInfo = {
  label: string
  kind: 'holiday' | 'special'
}

export const DatePicker = ({
  label,
  value,
  onChange,
  min,
  max,
  locale = 'cs-CZ',
  disabled,
  required,
  error,
  hint,
  className,
  getDayInfo,
}: DatePickerProps) => {
  const id = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const selectedDate = parseDateValue(value)
  const minDate = parseDateValue(min)
  const maxDate = parseDateValue(max)
  const [open, setOpen] = useState(false)
  const [visibleMonth, setVisibleMonth] = useState(
    () => selectedDate ?? minDate ?? new Date()
  )

  useEffect(() => {
    if (selectedDate) {
      setVisibleMonth(selectedDate)
    }
  }, [value])

  useEffect(() => {
    if (!open) return undefined
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  const weekdays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) => {
        const date = new Date(2024, 0, index + 1)
        return {
          key: formatDateValue(date),
          label: new Intl.DateTimeFormat(locale, {
            weekday: 'short',
          }).format(date),
        }
      }),
    [locale]
  )
  const days = monthDays(visibleMonth)
  const message = error ?? hint
  const messageId = message ? `${id}-message` : undefined
  const today = new Date()
  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  }).format(visibleMonth)
  const displayValue = selectedDate
    ? new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(selectedDate)
    : 'Vyberte datum'
  const previousMonthEnd = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    0
  )
  const nextMonthStart = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth() + 1,
    1
  )

  const changeMonth = (offset: number) => {
    setVisibleMonth(
      (current) =>
        new Date(current.getFullYear(), current.getMonth() + offset, 1)
    )
  }

  return (
    <FieldContainer className={className}>
      <FieldLabel id={`${id}-label`}>
        {label}
        {required ? ' *' : ''}
      </FieldLabel>
      <DatePickerContainer ref={containerRef}>
        <DatePickerTrigger
          type="button"
          disabled={disabled}
          $invalid={Boolean(error)}
          aria-labelledby={`${id}-label`}
          aria-describedby={messageId}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span>{displayValue}</span>
          <CalendarMark aria-hidden="true" />
        </DatePickerTrigger>
        {open && (
          <CalendarPopover
            role="dialog"
            aria-modal="false"
            aria-label={`Kalendář: ${label}`}
          >
            <CalendarHeader>
              <CalendarNavigationButton
                type="button"
                aria-label="Předchozí měsíc"
                disabled={Boolean(minDate && previousMonthEnd < minDate)}
                onClick={() => changeMonth(-1)}
              >
                Předchozí
              </CalendarNavigationButton>
              <strong>{monthLabel}</strong>
              <CalendarNavigationButton
                type="button"
                aria-label="Následující měsíc"
                disabled={Boolean(maxDate && nextMonthStart > maxDate)}
                onClick={() => changeMonth(1)}
              >
                Další
              </CalendarNavigationButton>
            </CalendarHeader>
            <CalendarWeek>
              {weekdays.map((weekday) => (
                <CalendarWeekday key={weekday.key}>
                  {weekday.label}
                </CalendarWeekday>
              ))}
            </CalendarWeek>
            <CalendarGrid role="grid">
              {days.map((calendarDay) => {
                if (!calendarDay.date) {
                  return <CalendarBlank key={calendarDay.key} />
                }
                const dayInfo = getDayInfo?.(calendarDay.date)
                const isToday = sameDay(today, calendarDay.date)
                const isSelected = sameDay(selectedDate, calendarDay.date)
                const formattedDate = new Intl.DateTimeFormat(locale, {
                  dateStyle: 'long',
                }).format(calendarDay.date)
                const accessibleLabel = [
                  formattedDate,
                  isToday ? 'dnes' : undefined,
                  dayInfo?.label,
                ]
                  .filter(Boolean)
                  .join(', ')

                return (
                  <CalendarDayContainer key={calendarDay.key}>
                    <CalendarDay
                      type="button"
                      role="gridcell"
                      $selected={isSelected}
                      $today={isToday}
                      $kind={dayInfo?.kind}
                      $weekend={[0, 6].includes(calendarDay.date.getDay())}
                      disabled={Boolean(
                        (minDate && calendarDay.date < minDate) ||
                          (maxDate && calendarDay.date > maxDate)
                      )}
                      aria-label={accessibleLabel}
                      aria-selected={isSelected}
                      title={dayInfo?.label}
                      onClick={() => {
                        onChange(formatDateValue(calendarDay.date as Date))
                        setOpen(false)
                      }}
                    >
                      <span>{calendarDay.date.getDate()}</span>
                      <CalendarDayIndicators aria-hidden="true">
                        {isToday && <CalendarDayIndicator $type="today" />}
                        {dayInfo && (
                          <CalendarDayIndicator $type={dayInfo.kind} />
                        )}
                      </CalendarDayIndicators>
                    </CalendarDay>
                    {dayInfo && (
                      <CalendarDayTooltip role="tooltip">
                        {dayInfo.label}
                      </CalendarDayTooltip>
                    )}
                  </CalendarDayContainer>
                )
              })}
            </CalendarGrid>
            {getDayInfo && (
              <CalendarLegend aria-label="Legenda kalendáře">
                <CalendarLegendItem $type="today">Dnes</CalendarLegendItem>
                <CalendarLegendItem $type="holiday">
                  Státní svátek
                </CalendarLegendItem>
                <CalendarLegendItem $type="special">
                  Speciální den
                </CalendarLegendItem>
                <CalendarLegendItem $type="weekend">Víkend</CalendarLegendItem>
              </CalendarLegend>
            )}
          </CalendarPopover>
        )}
      </DatePickerContainer>
      {message && (
        <FieldMessage
          id={messageId}
          $invalid={Boolean(error)}
          role={error ? 'alert' : undefined}
        >
          {message}
        </FieldMessage>
      )}
    </FieldContainer>
  )
}
